import React, { useState, useEffect, useMemo } from 'react';
import { FiAward, FiPlus, FiX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import {
  getNominations,
  createNomination,
  getNominationCategories,
  getTeamMembers
} from '../services/api';

const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', fg: '#92400e' },
  Shortlisted: { bg: '#dbeafe', fg: '#1e40af' },
  Selected: { bg: '#dcfce7', fg: '#166534' },
  Archived: { bg: '#f1f5f9', fg: '#475569' },
};

function Nominations() {
  const { user } = useAuth();
  const [nominations, setNominations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reasonMin, setReasonMin] = useState(30);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [nomRes, teamRes, catRes] = await Promise.all([
        getNominations(),
        getTeamMembers(),
        getNominationCategories(),
      ]);
      setNominations(nomRes.data.nominations || []);
      setTeamMembers(teamRes.data.team_members || []);
      setCategories(catRes.data.categories || []);
      setReasonMin(catRes.data.reason_min_length || 30);
    } catch (err) {
      console.error('Failed to load nominations', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitted = () => {
    setShowForm(false);
    loadAll();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiAward /> Leadership Recognition
          </h1>
          <p style={{ color: '#666', marginTop: '6px' }}>
            Nominate a teammate who showed leadership, ownership, or excellence.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 16px', backgroundColor: '#1a73e8', color: 'white',
            border: 'none', borderRadius: '6px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer'
          }}
        >
          <FiPlus /> New Nomination
        </button>
      </div>

      <h2 style={{ fontSize: '16px', color: '#333', marginBottom: '12px' }}>My Submissions</h2>

      {loading ? (
        <div style={{ color: '#666' }}>Loading…</div>
      ) : nominations.length === 0 ? (
        <div style={{ padding: '30px', textAlign: 'center', color: '#666', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
          You haven't submitted any nominations yet.
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', textAlign: 'left' }}>
                <th style={th}>Date</th>
                <th style={th}>Nominee</th>
                <th style={th}>Category</th>
                <th style={th}>Status</th>
                <th style={th}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {nominations.map((n) => {
                const colors = STATUS_COLORS[n.status] || STATUS_COLORS.Pending;
                return (
                  <tr key={n.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={td}>{new Date(n.created_at + 'Z').toLocaleDateString()}</td>
                    <td style={td}>{n.nominee_name}</td>
                    <td style={td}>{n.category}</td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                        fontWeight: 600, backgroundColor: colors.bg, color: colors.fg
                      }}>
                        {n.status}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#444', maxWidth: '420px' }}>{n.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NominationForm
          currentUserId={user?.id}
          teamMembers={teamMembers}
          categories={categories}
          reasonMin={reasonMin}
          onClose={() => setShowForm(false)}
          onSubmitted={handleSubmitted}
        />
      )}
    </div>
  );
}

function NominationForm({ currentUserId, teamMembers, categories, reasonMin, onClose, onSubmitted }) {
  const [nomineeId, setNomineeId] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [project, setProject] = useState('');
  const [dateOfAction, setDateOfAction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const candidates = useMemo(
    () => teamMembers.filter((m) => m.id !== currentUserId),
    [teamMembers, currentUserId]
  );

  const trimmedReason = reason.trim();
  const reasonRemaining = Math.max(0, reasonMin - trimmedReason.length);
  const canSubmit =
    nomineeId && category && trimmedReason.length >= reasonMin && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await createNomination({
        nominee_id: parseInt(nomineeId, 10),
        category,
        reason: trimmedReason,
        project_or_job_name: project.trim() || null,
        date_of_action: dateOfAction || null,
      });
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit nomination');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '8px', padding: '24px',
          width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>New Nomination</h2>
          <button onClick={onClose} style={iconBtn}><FiX /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <Field label="Nominee *">
            <select value={nomineeId} onChange={(e) => setNomineeId(e.target.value)} style={input} required>
              <option value="">Select an employee…</option>
              {candidates.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Category *">
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={input} required>
              <option value="">Select a category…</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>

          <Field
            label="Reason *"
            hint={`Describe a specific action or behavior — not how popular they are. Min ${reasonMin} characters.`}
          >
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
              required
            />
            <div style={{ fontSize: '12px', color: reasonRemaining ? '#b45309' : '#16a34a', marginTop: '4px' }}>
              {reasonRemaining
                ? `${reasonRemaining} more character${reasonRemaining === 1 ? '' : 's'} needed`
                : 'Minimum length met'}
            </div>
          </Field>

          <Field label="Project or Job Name (optional)">
            <input
              type="text"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              style={input}
              maxLength={200}
            />
          </Field>

          <Field label="Date of Action (optional)">
            <input
              type="date"
              value={dateOfAction}
              max={today}
              onChange={(e) => setDateOfAction(e.target.value)}
              style={input}
            />
          </Field>

          {error && (
            <div style={{ padding: '10px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
            <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            >
              {submitting ? 'Submitting…' : 'Submit Nomination'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
        {label}
      </label>
      {hint && <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>{hint}</div>}
      {children}
    </div>
  );
}

const th = { padding: '10px 14px', fontSize: '12px', color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '10px 14px', fontSize: '14px', color: '#222', verticalAlign: 'top' };
const input = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', backgroundColor: '#1a73e8', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '4px' };

export default Nominations;
